import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// File utilities - handles ad blocker bypass using blob URLs
export const fileUtils = {
  // View file in new tab (fetches via API, creates blob URL)
  viewFile: async (fileUrl, fileName) => {
    try {
      const fullUrl = `${BACKEND_URL}${fileUrl}`;
      const token = localStorage.getItem('token');
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Create a hidden link and click it (more reliable than window.open)
      const link = document.createElement('a');
      link.href = blobUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup blob URL after delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error('Error viewing file:', error);
      alert('Unable to view file. Please try again or disable ad blocker.');
    }
  },
  
  // Download file (fetches via API, triggers download)
  downloadFile: async (fileUrl, fileName) => {
    try {
      const fullUrl = `${BACKEND_URL}${fileUrl}?download=true`;
      const token = localStorage.getItem('token');
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Create temporary download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'download';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Unable to download file. Please try again or disable ad blocker.');
    }
  }
};

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
};

export const userAPI = {
  getUsers: () => api.get('/users'),
  createUser: (userData) => api.post('/users', userData),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  updateUser: (userId, userData) => api.put(`/users/${userId}`, userData),
};

export const staffAPI = {
  getStaff: () => api.get('/users/staff'),
  createStaff: (data) => api.post('/users/staff', data),
  updateStaff: (id, data) => api.put(`/users/staff/${id}`, data),
  deleteStaff: (id) => api.delete(`/users/${id}`),
};

export const designationAPI = {
  getDesignations: () => api.get('/users/designations'),
  createDesignation: (data) => api.post('/users/designations', data),
  updateDesignation: (id, data) => api.put(`/users/designations/${id}`, data),
  deleteDesignation: (id) => api.delete(`/users/designations/${id}`),
};

export const contractorPaymentAPI = {
  create: (data) => api.post('/contractor-payments', data),
  getAll: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return api.get(`/contractor-payments${qs ? `?${qs}` : ''}`);
  },
  getSummary: () => api.get('/contractor-payments/summary'),
  markPaid: (id, data) => api.patch(`/contractor-payments/${id}/mark-paid`, data),
  markUnpaid: (id) => api.patch(`/contractor-payments/${id}/mark-unpaid`),
  waive: (id) => api.patch(`/contractor-payments/${id}/waive`),
};

export const clientAPI = {
  getClients: () => api.get('/clients'),
  createClient: (clientData) => api.post('/clients', clientData),
  getClient: (id) => api.get(`/clients/${id}`),
  updateClient: (id, data) => api.put(`/clients/${id}`, data),
  createCaseForClient: (clientId, data) => api.post(`/clients/${clientId}/create-case`, data),
  getClientCase: (clientId) => api.get(`/cases?client_id=${clientId}`),
  resetPortalPassword: (clientId) => api.post(`/clients/${clientId}/reset-portal-password`),
};

export const caseAPI = {
  getCases: () => api.get('/cases'),
  getCase: (id) => api.get(`/cases/${id}`),
  getMyCases: () => api.get('/cases'),  // Returns filtered cases for the logged-in user
  createCase: (caseData) => api.post('/cases', caseData),
  updateCase: (id, data) => api.patch(`/cases/${id}`, data),
  changeVisaProgram: (caseId, visaProgramId) => api.put(`/cases/${caseId}/change-visa-program?visa_program_id=${visaProgramId}`),
};

export const visaProgramAPI = {
  getPrograms: () => api.get('/visa-programs'),
  getProgram: (id) => api.get(`/visa-programs/${id}`),
  createProgram: (data) => api.post('/visa-programs', data),
  updateProgram: (id, data) => api.put(`/visa-programs/${id}`, data),
  deleteProgram: (id) => api.delete(`/visa-programs/${id}`),
};

export const checklistAPI = {
  getChecklistByProgram: (programId) => api.get(`/checklist-templates/visa-program/${programId}`),
  createTemplate: (data) => api.post('/checklist-templates', data),
  getAllTemplates: () => api.get('/checklist-templates'),
};

export const documentAPI = {
  getCaseDocuments: (caseId) => api.get(`/documents/case/${caseId}`),
  getByCaseId: (caseId) => api.get(`/documents/case/${caseId}`),  // Alias for compatibility
  uploadDocument: (data) => api.post('/documents', data),
  uploadDocumentWithFile: (formData) => api.post('/documents/upload-file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateDocument: (id, data) => api.patch(`/documents/${id}`, data),
  getDocument: (id) => api.get(`/documents/${id}`),
  clientReview: (id, data) => api.post(`/documents/${id}/client-review`, data),
};

export const legalAPI = {
  // Escalations
  createEscalation:   (data)           => api.post('/legal/escalations', data),
  listEscalations:    (params = {})    => api.get('/legal/escalations', { params }),
  getEscalation:      (id)             => api.get(`/legal/escalations/${id}`),
  updateEscalation:   (id, data)       => api.patch(`/legal/escalations/${id}`, data),
  // Notes
  addNote:            (id, data)       => api.post(`/legal/escalations/${id}/notes`, data),
  getNotes:           (id)             => api.get(`/legal/escalations/${id}/notes`),
  getEvidence:        (id)             => api.get(`/legal/escalations/${id}/evidence`),
  // Analytics
  getAnalytics:       (days = 90)      => api.get('/legal/analytics', { params: { days } }),
};

export const completeFileAPI = {
  get:              (caseId)        => api.get(`/cases/${caseId}/complete-file`),
  generate:         (caseId)        => api.post(`/cases/${caseId}/complete-file/generate`),
  sendToClient:     (caseId)        => api.post(`/cases/${caseId}/complete-file/send-to-client`),
  uploadGovForm:    (caseId, fd)    => api.post(`/cases/${caseId}/complete-file/government-forms`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteGovForm:    (caseId, fmId)  => api.delete(`/cases/${caseId}/complete-file/government-forms/${fmId}`),
  approve:          (caseId, data)  => api.post(`/cases/${caseId}/complete-file/approve`, data),
  reject:           (caseId, data)  => api.post(`/cases/${caseId}/complete-file/reject`, data),
  getLegalRecord:   (caseId)        => api.get(`/cases/${caseId}/complete-file/legal-record`),
};

export const familyAPI = {
  // Family composition
  updateComposition: (caseId, data) => api.patch(`/cases/${caseId}/family-composition`, data),

  // Family members
  addMember: (caseId, data) => api.post(`/cases/${caseId}/family-members`, data),
  updateMember: (caseId, memberId, data) => api.patch(`/cases/${caseId}/family-members/${memberId}`, data),
  deleteMember: (caseId, memberId) => api.delete(`/cases/${caseId}/family-members/${memberId}`),

  // Document summary (per-applicant progress)
  getDocumentSummary: (caseId) => api.get(`/cases/${caseId}/document-summary`),

  // Custom document requests (officer → client)
  getCustomRequests: (caseId) => api.get(`/cases/${caseId}/custom-document-requests`),
  addCustomRequest: (caseId, data) => api.post(`/cases/${caseId}/custom-document-requests`, data),
  deleteCustomRequest: (caseId, requestId) => api.delete(`/cases/${caseId}/custom-document-requests/${requestId}`),
};

export const sampleDocAPI = {
  getCaseSamples: (caseId) => api.get(`/sample-documents/case/${caseId}`),
  uploadSample: (formData) => api.post('/sample-documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const fileAPI = {
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getFileUrl: (filename) => `${API_BASE}/files/${filename}`,
};

export const b2bPartnerAPI = {
  getPartners: () => api.get('/b2b-partners'),
  getPartner: (id) => api.get(`/b2b-partners/${id}`),
  createPartner: (data) => api.post('/b2b-partners', data),
  updatePartner: (id, data) => api.put(`/b2b-partners/${id}`, data),
  deletePartner: (id) => api.delete(`/b2b-partners/${id}`),
};

export const activityAPI = {
  getCaseTimeline: (caseId) => api.get(`/activity-logs/case/${caseId}`),
};

export const agreementAPI = {
  getByCase: (caseId) => api.get(`/agreements/case/${caseId}`),
  sign: (caseId, data) => api.post(`/agreements/case/${caseId}/sign`, data),
};

export const notificationAPI = {
  getNotifications: (unreadOnly = false) => api.get(`/notifications?unread_only=${unreadOnly}`),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationId) => api.patch(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),
  deleteNotification: (notificationId) => api.delete(`/notifications/${notificationId}`),
  clearAll: () => api.delete('/notifications/clear-all'),
};

export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getFiltered: (days = 30, startDate = null, endDate = null) => {
    let url = `/analytics/dashboard-filtered?days=${days}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    return api.get(url);
  },
  getSuccessRatio: (days = 30, startDate = null, endDate = null, officerId = null) => {
    let url = `/analytics/success-ratio?days=${days}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    if (officerId) {
      url += `&officer_id=${officerId}`;
    }
    return api.get(url);
  },
};

export const complaintAPI = {
  getComplaints: () => api.get('/complaints'),
  createComplaint: (data) => api.post('/complaints', data),
  updateComplaint: (id, data) => api.patch(`/complaints/${id}`, data),
  getStats: (days = 30, startDate = null, endDate = null) => {
    let url = `/complaints/stats?days=${days}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    return api.get(url);
  },
  // Complaint Natures (Types) CRUD
  getNatures: () => api.get('/complaint-natures'),
  getAllNatures: () => api.get('/complaint-natures/all'),
  createNature: (data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description || '');
    return api.post('/complaint-natures', formData);
  },
  updateNature: (id, data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description || '');
    formData.append('is_active', data.is_active);
    return api.put(`/complaint-natures/${id}`, formData);
  },
  deleteNature: (id) => api.delete(`/complaint-natures/${id}`),
};

export const caseServiceAPI = {
  extendService: (caseId, months) => api.post(`/cases/${caseId}/extend?extension_months=${months}`),
  getTimeRemaining: (caseId) => api.get(`/cases/${caseId}/time-remaining`),
  getProcessStages: (caseId) => api.get(`/cases/${caseId}/process-stages`),
};

export const csatAPI = {
  submitRating: (caseId, rating, feedback) => api.post(`/csat/submit?case_id=${caseId}&rating=${rating}&feedback=${feedback}`),
  getOfficerCSAT: (officerId, days = 30) => api.get(`/csat/officer/${officerId}?days=${days}`),
  getCompanyCSAT: (days = 30) => api.get(`/csat/company?days=${days}`),
  getMyCSAT: (days = 30) => api.get(`/csat/my-score?days=${days}`),
};

// Email Service APIs
export const emailAPI = {
  // Email logs (original)
  getLogs: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/email-logs?${queryParams.toString()}`);
  },
  getStats: () => api.get('/email-logs/stats'),
  retryEmail: (emailId) => api.post(`/email-logs/${emailId}/retry`),
  processQueue: (batchSize = 5) => api.post(`/email-queue/process?batch_size=${batchSize}`),
  getTemplates: () => api.get('/email-templates'),
  getTemplate: (type) => api.get(`/email-templates/${type}`),
  saveTemplate: (data) => api.post('/email-templates', data),
  deleteTemplate: (type) => api.delete(`/email-templates/${type}`),
  triggerReminders: () => api.post('/cases/reminders'),
  
  // Email module (new)
  getEmails: (folder, caseId) => api.get(`/emails?folder=${folder}${caseId ? `&case_id=${caseId}` : ''}`),
  getEmailDetail: (emailId) => api.get(`/emails/${emailId}`),
  sendEmail: (data) => {
    const formData = new FormData();
    formData.append('to_email', data.to_email);
    formData.append('subject', data.subject);
    formData.append('body', data.body);
    if (data.case_id) formData.append('case_id', data.case_id);
    if (data.client_id) formData.append('client_id', data.client_id);
    // Support for attachments
    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach(file => {
        formData.append('attachments', file);
      });
    }
    return api.post('/emails/compose', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  saveDraft: (data) => {
    const formData = new FormData();
    if (data.to_email) formData.append('to_email', data.to_email);
    if (data.subject) formData.append('subject', data.subject);
    if (data.body) formData.append('body', data.body);
    if (data.case_id) formData.append('case_id', data.case_id);
    if (data.client_id) formData.append('client_id', data.client_id);
    if (data.draft_id) formData.append('draft_id', data.draft_id);
    return api.post('/emails/draft', formData);
  },
  deleteDraft: (draftId) => api.delete(`/emails/draft/${draftId}`),
  getCaseEmails: (caseId) => api.get(`/emails/case/${caseId}`),
  getUnreadCount: () => api.get('/emails/unread-count'),
};

// Zoho Sign APIs
export const signAPI = {
  getStatus: () => api.get('/zoho-sign/status'),
  sendForSigning: (caseId) => api.post(`/cases/${caseId}/send-agreement-for-signing`),
  getCaseRequests: (caseId) => api.get(`/sign-requests/case/${caseId}`),
  getRequestStatus: (requestId) => api.get(`/sign-requests/${requestId}/status`),
  sendReminder: (requestId) => api.post(`/sign-requests/${requestId}/remind`),
  recallRequest: (requestId) => api.post(`/sign-requests/${requestId}/recall`),
  downloadSigned: (requestId) => api.get(`/sign-requests/${requestId}/download`, { responseType: 'blob' }),
};

// Case Timeline & Notes APIs
export const timelineAPI = {
  getTimeline: (caseId) => api.get(`/cases/${caseId}/timeline`),
};

export const notesAPI = {
  getNotes: (caseId) => api.get(`/cases/${caseId}/notes`),
  addNote: (caseId, note) => api.post(`/cases/${caseId}/notes`, { note }),
  deleteNote: (caseId, noteId) => api.delete(`/cases/${caseId}/notes/${noteId}`),
};

// System Status APIs
export const systemAPI = {
  getEmailStatus: () => api.get('/system/email-status'),
  getIntegrationSettings: () => api.get('/integration-settings'),
  updateIntegrationSettings: (data) => api.post('/integration-settings', data),
  testSmtp: () => api.post('/integration-settings/test-smtp'),
  testZohoSign: () => api.post('/integration-settings/test-zoho-sign'),
  getCompanySettings: () => api.get('/company-settings'),
  updateCompanySettings: (data) => api.put('/company-settings', data),
};

// Accounting APIs
export const accountingAPI = {
  // Dashboard
  getDashboard: (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const qs = params.toString();
    return api.get(`/accounting/dashboard${qs ? `?${qs}` : ''}`);
  },
  
  // Sales
  getSales: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/accounting/sales?${queryParams.toString()}`);
  },
  createSale: (data) => api.post('/accounting/sales', data),
  updateSale: (saleId, data) => api.put(`/accounting/sales/${saleId}`, data),
  deleteSale: (saleId) => api.delete(`/accounting/sales/${saleId}`),
  getSalesAnalytics: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/accounting/sales/analytics?${queryParams.toString()}`);
  },
  getSalesDistribution: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/accounting/sales/distribution?${queryParams.toString()}`);
  },
  
  // Expenses
  getExpenses: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/accounting/expenses?${queryParams.toString()}`);
  },
  createExpense: (formData) => api.post('/accounting/expenses', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateExpense: (expenseId, formData) => api.put(`/accounting/expenses/${expenseId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteExpense: (expenseId) => api.delete(`/accounting/expenses/${expenseId}`),
  
  // Expense payment
  markExpensePaid: (expenseId, data = {}) => {
    const params = new URLSearchParams();
    if (data.paid_date) params.append('paid_date', data.paid_date);
    if (data.paid_amount) params.append('paid_amount', data.paid_amount);
    if (data.payment_method) params.append('payment_method', data.payment_method);
    if (data.payment_reference) params.append('payment_reference', data.payment_reference);
    return api.post(`/accounting/expenses/${expenseId}/mark-paid?${params.toString()}`);
  },
  markExpenseUnpaid: (expenseId) => api.post(`/accounting/expenses/${expenseId}/mark-unpaid`),
  
  // Carry forward
  carryForwardExpenses: (targetMonth) => api.post(`/accounting/expenses/carry-forward?target_month=${targetMonth}`),
  getMonthlyReport: (month) => {
    const [year, mon] = month.split('-');
    return api.get(`/accounting/reports/monthly?year=${year}&month=${parseInt(mon, 10)}`);
  },
  
  getExpenseAnalytics: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/accounting/expenses/analytics?${queryParams.toString()}`);
  },
  getExpenseCategories: () => api.get('/accounting/expense-categories'),
  
  // Forecast
  getForecast: (months = 3) => api.get(`/accounting/forecast?months=${months}`),
  
  // Reports
  getSalesReport: (startDate, endDate) => api.get(`/accounting/reports/sales?start_date=${startDate}&end_date=${endDate}`),
  getExpenseReport: (startDate, endDate) => api.get(`/accounting/reports/expenses?start_date=${startDate}&end_date=${endDate}`),
  getPnLReport: (startDate, endDate) => api.get(`/accounting/reports/pnl?start_date=${startDate}&end_date=${endDate}`),
  getOutstandingReport: (startDate, endDate) => api.get(`/accounting/reports/outstanding${startDate ? `?start_date=${startDate}&end_date=${endDate}` : ''}`),
  
  // Payment Sync
  syncPayment: (caseId, amountReceived, paymentDate) => 
    api.post(`/cases/${caseId}/sync-payment?amount_received=${amountReceived}${paymentDate ? `&payment_date=${paymentDate}` : ''}`),
};

// Sales Leads APIs
export const leadsAPI = {
  getLeads: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))).toString();
    return api.get(`/leads${qs ? `?${qs}` : ''}`);
  },
  createLead: (data) => api.post('/leads', data),
  getLead: (id) => api.get(`/leads/${id}`),
  updateLead: (id, data) => api.put(`/leads/${id}`, data),
  updateStage: (id, data) => api.patch(`/leads/${id}/stage`, data),
  deleteLead: (id) => api.delete(`/leads/${id}`),
  assignLead: (id, consultantId) => api.patch(`/leads/${id}/assign?consultant_id=${consultantId}`),
  logCall: (id, data) => api.post(`/leads/${id}/calls`, data),
  getCalls: (id) => api.get(`/leads/${id}/calls`),
  addNote: (id, content) => api.post(`/leads/${id}/notes`, { content }),
  getNotes: (id) => api.get(`/leads/${id}/notes`),
  getActivities: (id) => api.get(`/leads/${id}/activities`),
  getEmails: (id) => api.get(`/leads/${id}/emails`),
  // Analytics
  getPipelineAnalytics: () => api.get('/leads/analytics/pipeline'),
  getFunnelAnalytics: () => api.get('/leads/analytics/funnel'),
  getTrends: (days = 30) => api.get(`/leads/analytics/trends?days=${days}`),
  getConsultantPerformance: () => api.get('/leads/analytics/consultant-performance'),
  getSourceBreakdown: () => api.get('/leads/analytics/sources'),
  getAgingSummary: () => api.get('/leads/analytics/aging'),
  // Bulk operations
  bulkAssign: (leadIds, consultantId) => api.post('/leads/bulk/assign', { lead_ids: leadIds, consultant_id: consultantId }),
  bulkStage: (leadIds, stage) => api.post('/leads/bulk/stage', { lead_ids: leadIds, stage }),
  // Follow-ups
  getOverdueFollowups: () => api.get('/leads/follow-ups/due'),
  getTodayFollowups: () => api.get('/leads/follow-ups/today'),
  // Lead Sources
  getSources: () => api.get('/leads/sources'),
  createSource: (data) => api.post('/leads/sources', data),
  deleteSource: (id) => api.delete(`/leads/sources/${id}`),
};

// Marketing APIs
export const marketingAPI = {
  // Stage-1: scan only (no import), returns duplicate analysis
  scanLeads: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/marketing/leads/scan', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Quick import (auto-marks duplicates, no per-row decisions)
  importLeads: (formData) => api.post('/marketing/leads/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // Stage-3: import with per-row decisions (import/skip/merge)
  importReviewed: (data) => api.post('/marketing/leads/import-reviewed', data),
  bulkAssign: (data) => api.post('/marketing/leads/bulk-assign', data),
  getImports: () => api.get('/marketing/imports'),
  getImport: (id) => api.get(`/marketing/imports/${id}`),
  deleteImport: (id) => api.delete(`/marketing/imports/${id}`),
  getLeadPool: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))).toString();
    return api.get(`/marketing/pool${qs ? `?${qs}` : ''}`);
  },
  assignFromPool: (leadIds, consultantId) => api.post('/marketing/leads/bulk-assign', { lead_ids: leadIds, consultant_id: consultantId }),
  getAnalytics: () => api.get('/marketing/analytics'),
  getDuplicateAnalytics: () => api.get('/marketing/duplicates/analytics'),
  getConsultants: () => api.get('/marketing/consultants'),
  getDuplicateGroup: (groupId) => api.get(`/marketing/duplicates/group/${groupId}`),
  getLeadEnquiryHistory: (leadId) => api.get(`/marketing/duplicates/lead/${leadId}`),
};

// Attendance APIs
export const attendanceAPI = {
  punchIn: () => api.post('/attendance/punch-in'),
  punchOut: (notes = '') => api.post(`/attendance/punch-out${notes ? `?notes=${encodeURIComponent(notes)}` : ''}`),
  breakStart: (reason = '') => api.post(`/attendance/break-start${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`),
  breakEnd: () => api.post('/attendance/break-end'),
  getToday: () => api.get('/attendance/today'),
  getHistory: (limit = 30) => api.get(`/attendance/history?limit=${limit}`),
  getTeam: () => api.get('/attendance/team'),
};

// Document Library APIs
export const libraryAPI = {
  getCategories: () => api.get('/library/categories'),
  createCategory: (data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description || '');
    return api.post('/library/categories', formData);
  },
  deleteCategory: (categoryId) => api.delete(`/library/categories/${categoryId}`),
  getDocuments: (categoryId) => api.get(`/library/documents${categoryId ? `?category_id=${categoryId}` : ''}`),
  uploadDocument: (data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description || '');
    formData.append('category_id', data.category_id);
    formData.append('file', data.file);
    return api.post('/library/documents', formData);
  },
  deleteDocument: (documentId) => api.delete(`/library/documents/${documentId}`),
  shareDocument: (documentId, caseId) => {
    const formData = new FormData();
    formData.append('case_id', caseId);
    return api.post(`/library/documents/${documentId}/share`, formData);
  },
};
